import { mountStoreDevtool } from 'simple-zustand-devtools'
import { create } from 'zustand'
import { combine } from 'zustand/middleware'
import { END_DATE_MONTH, FIRST_MONTH_DAY } from 'utils/variablesAndSelects'
import { ListDocumentType, ReponseGetDocumentApprovalFile } from 'types/documentos'
import { DocumentFilterType } from 'pages/documentManagement/sidebars/schema'

export type DocumentToUploadType = {
  file: File | null
  id_concc070: number
  id_corcc080: number
  salvar_arquivo: boolean
  nm_documento: string
}

type DocumentDeleteType = {
  id_documento: number
  id_corcc080: number
}

type DocumentEditType = {
  id_documento: number
  id_corcc080: number
  st_status: 'P' | 'A' | 'R' | 'E'
}
interface DocumentsApprovalInfo {
  defaultFilter: DocumentFilterType
  currentFilter: DocumentFilterType
  proposalsData: ListDocumentType[]
  sortedProposalsData: ListDocumentType[]
  proposal?: ListDocumentType
  stage: {
    delete: DocumentDeleteType[]
    edit: DocumentEditType[]
  }
  documentToUpload: DocumentToUploadType
  documentToDownload: ReponseGetDocumentApprovalFile
}

const DEFAULT_FILTER: DocumentFilterType = {
  dt_venda: {
    dt_venda_inicial: FIRST_MONTH_DAY,
    dt_venda_final: END_DATE_MONTH
  },
  id_documento: { id_documento_inicial: null, id_documento_final: null },
  cd_inscricao_nacional: '',
  cpf_cnpj_comissionado: '',
  st_status: 'T'
}

const DEFAULT_DOCUMENT_APPROVAL_INFO: DocumentsApprovalInfo = {
  defaultFilter: DEFAULT_FILTER,
  currentFilter: DEFAULT_FILTER,
  proposalsData: [],
  sortedProposalsData: [],
  proposal: {} as ListDocumentType,
  stage: {
    delete: [],
    edit: []
  },
  documentToUpload: {
    file: null,
    id_concc070: 0,
    id_corcc080: 0,
    salvar_arquivo: false,
    nm_documento: ''
  },
  documentToDownload: {} as ReponseGetDocumentApprovalFile
}

const useDocumentsManagementStore = create(
  combine(DEFAULT_DOCUMENT_APPROVAL_INFO, (set) => ({
    setCurrentFilter: (currentFilter: DocumentFilterType) => set({ currentFilter }),
    setProduct: (proposal: ListDocumentType) => set({ proposal }),
    setDocumentToUpload: (documentToUpload: DocumentsApprovalInfo['documentToUpload']) =>
      set({ documentToUpload }),
    setProposalsData: (proposalsData: DocumentsApprovalInfo['proposalsData']) =>
      set({ proposalsData }),
    setSortedProposalsData: (sortedProposalsData: DocumentsApprovalInfo['sortedProposalsData']) =>
      set({ sortedProposalsData }),
    setDocumentToDownload: (documentToDownload: DocumentsApprovalInfo['documentToDownload']) =>
      set({ documentToDownload }),
    includeInStage: (data: DocumentEditType | DocumentDeleteType) => {
      set((state) => {
        const { id_corcc080 } = data

        if ('st_status' in data) {
          const editIndex = state.stage.edit.findIndex((item) => item.id_corcc080 === id_corcc080)
          if (editIndex !== -1) {
            const updatedEdit = [...state.stage.edit]
            updatedEdit[editIndex] = data as DocumentEditType
            return {
              stage: {
                ...state.stage,
                edit: updatedEdit
              }
            }
          } else {
            return {
              stage: {
                ...state.stage,
                edit: [...state.stage.edit, data as DocumentEditType]
              }
            }
          }
        } else {
          const deleteIndex = state.stage.delete.findIndex(
            (item) => item.id_corcc080 === id_corcc080
          )

          if (deleteIndex !== -1) {
            const updatedDelete = [...state.stage.delete]
            updatedDelete[deleteIndex] = data as DocumentDeleteType

            return {
              stage: {
                ...state.stage,
                delete: updatedDelete
              }
            }
          } else {
            return {
              stage: {
                ...state.stage,
                delete: [...state.stage.delete, data as DocumentDeleteType]
              }
            }
          }
        }
      })
    },

    deleteFromStage: (id_corcc080: number, stageType: 'delete' | 'edit') => {
      set((state) => {
        const updatedStage = {
          ...state.stage,
          [stageType]: state.stage[stageType].filter((item) => item.id_corcc080 !== id_corcc080)
        }

        return {
          stage: {
            ...state.stage,
            ...updatedStage
          }
        }
      })
    },
    clearStage: () => set({ proposal: {} as ListDocumentType, stage: { delete: [], edit: [] } }),
    clearProposal: () => set({ proposal: {} as ListDocumentType }),
    resetFilter: () => set({ currentFilter: DEFAULT_FILTER })
  }))
)

if (process.env.NODE_ENV === 'development') {
  mountStoreDevtool('Documents Management Store', useDocumentsManagementStore)
}

export default useDocumentsManagementStore
